import type { LucideIcon } from 'lucide-react';
import {
  BookOpen,
  Compass,
  HandCoins,
  Heart,
  Layers,
  LayoutDashboard,
  Package,
  Settings,
  Shield,
  User,
  Users,
} from 'lucide-react';

export type NavBadge = 'friends' | 'admin';

export interface NavItem {
  id: string;
  label: string;
  to: string;
  icon: LucideIcon;
  badge?: NavBadge;
}

export interface NavSection {
  id: string;
  label: string;
  items: NavItem[];
}

/** Where the user is: path plus URL parameters (?tab=...). */
export interface NavLocation {
  pathname: string;
  search: string;
}

export const COLLECTION_ITEMS: NavItem[] = [
  { id: 'cards', label: 'Cartes', to: '/portfolio', icon: Layers },
  { id: 'sets', label: 'Sets', to: '/portfolio?tab=sets', icon: Package },
  { id: 'pokedex', label: 'Pokédex', to: '/portfolio?tab=pokedex', icon: BookOpen },
  { id: 'wishlist', label: 'Wishlist', to: '/portfolio?tab=wishlist', icon: Heart },
  { id: 'sold', label: 'Vendues', to: '/sold', icon: HandCoins },
];

export const GENERAL_ITEMS: NavItem[] = [
  { id: 'dashboard', label: 'Dashboard', to: '/dashboard', icon: LayoutDashboard },
  { id: 'friends', label: 'Amis', to: '/friends', icon: Users, badge: 'friends' },
  { id: 'discover', label: 'Découvrir', to: '/decouvrir', icon: Compass },
];

export const ADMIN_ITEM: NavItem = {
  id: 'admin',
  label: 'Admin',
  to: '/admin',
  icon: Shield,
  badge: 'admin',
};

export const ACCOUNT_ITEMS: NavItem[] = [
  { id: 'profile', label: 'Profil', to: '/profile?tab=personal', icon: User },
  { id: 'settings', label: 'Paramètres', to: '/profile?tab=settings', icon: Settings },
];

/** Sections at the top of the sidebar (the Admin link only shows for admins). */
export function getMainSections(isAdmin: boolean): NavSection[] {
  return [
    { id: 'collection', label: 'Collection', items: COLLECTION_ITEMS },
    {
      id: 'general',
      label: 'Navigation',
      items: isAdmin ? [...GENERAL_ITEMS, ADMIN_ITEM] : GENERAL_ITEMS,
    },
  ];
}

/** Whether a navigation link matches the page being shown. */
export function isNavItemActive(item: NavItem, { pathname, search }: NavLocation): boolean {
  const tab = new URLSearchParams(search).get('tab');

  switch (item.id) {
    case 'cards':
      return pathname === '/portfolio' && tab !== 'sets' && tab !== 'pokedex' && tab !== 'wishlist';
    case 'sets':
      return (
        (pathname === '/portfolio' && tab === 'sets') || pathname.startsWith('/portfolio/set/')
      );
    case 'pokedex':
      return (pathname === '/portfolio' && tab === 'pokedex') || pathname.startsWith('/pokemon/');
    case 'wishlist':
      return pathname === '/portfolio' && tab === 'wishlist';
    case 'sold':
      return pathname.startsWith('/sold');
    case 'profile':
      return pathname === '/profile' && tab !== 'settings';
    case 'settings':
      return pathname === '/profile' && tab === 'settings';
    default:
      return pathname.startsWith(item.to);
  }
}
